// Client mirror of the published-practice envelope. The server
// (server/param-envelope.js) is the authority and carries the citations;
// this holds only what the param card needs to render the note, the same
// way params.js mirrors band logic for the cards.
//
// Keep the bounds in step with the server. test/param-envelope.test.js
// asserts they match.
import { formatSalinity, salinityUnitLabel } from './salinity.js';

export const PARAM_ENVELOPE = {
  alk: { low: 6.5, high: 12.0, unit: 'dKH', label: 'alkalinity' },
  calcium: { low: 380, high: 500, unit: 'ppm', label: 'calcium' },
  magnesium: { low: 1200, high: 1500, unit: 'ppm', label: 'magnesium' },
  ph: { low: 7.7, high: 8.6, unit: '', label: 'pH' },
  salinity: { low: 32, high: 37, unit: 'ppt', label: 'salinity' },
};

// Mirrors server/water-type.js: the envelope is a reef citation, so a
// freshwater tank never renders it (reefer-review finding W3).
const FRESHWATER_SUBTYPES = new Set([
  'planted-high',
  'planted-low',
  'community',
  'cichlid',
  'shrimp',
  'other-freshwater',
]);

export function envelopeBreached(paramKey, value, subtypeSlug = null) {
  if (FRESHWATER_SUBTYPES.has(String(subtypeSlug || ''))) return false;
  const env = PARAM_ENVELOPE[paramKey];
  if (!env) return false;
  if (value === null || value === undefined || value === '') return false;
  const v = Number(value);
  if (!Number.isFinite(v)) return false;
  return v < env.low || v > env.high;
}

// Says three things at once: the score is not passing, why, and that the
// keeper's own target is untouched. The card around this line still shows
// their band, so without the last clause it reads as a contradiction.
// Salinity reads in the keeper's scale: "32 to 37 ppt" sat on a card in S.G.
export function envelopeNote(paramKey, { salinityUnit = 'ppt' } = {}) {
  const env = PARAM_ENVELOPE[paramKey];
  if (!env) return null;
  const sal = paramKey === 'salinity' && salinityUnit !== 'ppt';
  const range = sal
    ? `${formatSalinity(env.low, salinityUnit)} to ${formatSalinity(env.high, salinityUnit)} ${salinityUnitLabel(salinityUnit)}`
    : env.unit
      ? `${env.low} to ${env.high} ${env.unit}`
      : `${env.low} to ${env.high}`;
  return `Outside the published range for ${env.label} (${range}). Not scored as passing. Your target is unchanged.`;
}
